// src/utils/dataCache.js
import * as pako from "pako";

class DataCache {
  constructor() {
    this.cache = new Map();
    this.expiration = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes
  }

  async get(url, options = {}) {
    const { ttl = this.defaultTTL, forceRefresh = false } = options;
    const now = Date.now();

    // Serve from cache if fresh
    if (!forceRefresh && this.cache.has(url) && this.expiration.get(url) > now) {
      console.log(`📦 Using cached data for: ${url}`);
      return this.cache.get(url);
    }

    try {
      console.log(`🌐 Fetching fresh data for: ${url}`);
      const res = await fetch(url, { mode: "cors", cache: "no-cache" });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

      const enc = (res.headers.get("content-encoding") || "").toLowerCase();

      let text;
      if (enc.includes("gzip") || enc.includes("br") || enc.includes("deflate")) {
        // Browser auto-decompressed → just read as text
        text = await res.text();
      } else if (url.endsWith(".gz")) {
        // Might be a raw .gz object without a header — detect magic bytes
        const buf = await res.arrayBuffer();
        const u8 = new Uint8Array(buf);
        const looksGzip = u8.length >= 2 && u8[0] === 0x1f && u8[1] === 0x8b;
        if (looksGzip) {
          try {
            text = pako.ungzip(u8, { to: "string" });
          } catch {
            // If it was already plain text, just decode
            text = new TextDecoder("utf-8").decode(u8);
          }
        } else {
          text = new TextDecoder("utf-8").decode(u8);
        }
      } else {
        text = await res.text();
      }

      this.cache.set(url, text);
      this.expiration.set(url, now + ttl);
      return text;
    } catch (err) {
      console.error(`❌ Failed to fetch ${url}:`, err);
      if (this.cache.has(url)) {
        console.log(`📦 Using expired cached data for: ${url}`);
        return this.cache.get(url);
      }
      throw err;
    }
  }

  clear() { this.cache.clear(); this.expiration.clear(); }

  clearExpired() {
    const now = Date.now();
    for (const [url, exp] of this.expiration.entries()) {
      if (exp <= now) { this.cache.delete(url); this.expiration.delete(url); }
    }
  }
}

export const dataCache = new DataCache();
window.__dataCache = dataCache.cache;

export async function fetchCSV(url, options = {}) {
  return await dataCache.get(url, options);
}
